import React, { createContext, useContext, useState } from 'react';
import {
  NotificationContextProps,
  NotificationProps,
  NotificationProviderProps,
  openNotificationType,
} from '@/components/notification/types';
import Authorization from '@/components/notification/authorization';
import TryAgain from '@/components/notification/try-again';
import OcrCustomError from '@/components/notification/ocr-custom-error';
import BrowserIncompatible from '@/components/notification/browser-incompatible';

export const NotificationContext = createContext<NotificationContextProps>({
  hasError: false,
  setError: () => {},
  setHasError: () => {},
  openNotification: 'authorization',
  setOpenNotification: () => {},
  errorCallback: null,
  setErrorCallback: () => {},
});

function Notification({ children }: NotificationProps) {
  const {
    hasError,
    openNotification,
    errorCallback,
    setHasError,
    setOpenNotification,
    setErrorCallback,
  } = useContext(NotificationContext);

  const handleError = () => {
    if (errorCallback) {
      errorCallback();
    }
    setHasError(false);
    setOpenNotification(null);
    setErrorCallback(null);
  };

  const handleBrowserIncompatibleRetry = () => {
    setHasError(false);
    setOpenNotification(null);
    setErrorCallback(null);
  };

  return (
    <>
      {!hasError && children}
      {hasError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
          {openNotification === 'authorization' && <Authorization />}
          {openNotification === 'orc-custom' && <OcrCustomError onRetry={handleError} />}
          {openNotification === 'try-again' && <TryAgain onRetry={handleError} />}
          {openNotification === 'browser-incompatible' && (
            <BrowserIncompatible onRetry={handleBrowserIncompatibleRetry} />
          )}
        </div>
      )}
    </>
  );
}

function NotificationProvider({ children }: NotificationProviderProps) {
  const [hasError, setHasError] = useState(false);
  const [openNotification, setOpenNotification] = useState<openNotificationType | null>(null);
  const [errorCallback, setErrorCallback] = useState<(() => void) | null>(null);

  const setError = (value: openNotificationType, callback?: () => void) => {
    setOpenNotification(value);
    setHasError(true);

    if (callback) {
      setErrorCallback(() => callback);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        hasError,
        setError,
        openNotification,
        errorCallback,
        setHasError,
        setOpenNotification,
        setErrorCallback,
      }}
    >
      <Notification>{children}</Notification>
    </NotificationContext.Provider>
  );
}

export default NotificationProvider;
